import {
  ToggleContainer
} from "./chunk-RW5RZAVN.js";
import {
  InputSubtext,
  defaultTextInputTypography,
  inputSpacingPx,
  textInputPlaceholderColor,
  textInputStyle
} from "./chunk-GEE2G7BL.js";
import {
  UnstyledButton
} from "./chunk-I5WDNJY3.js";
import {
  Tooltip
} from "./chunk-XADWD755.js";
import {
  z
} from "./chunk-55OQPFLF.js";
import {
  CheckboxContainer
} from "./chunk-SPIRL6S7.js";
import {
  Icon,
  IconContainer
} from "./chunk-RMZQSXO2.js";
import {
  standardBorderRadius
} from "./chunk-RNCUEVKR.js";
import {
  applyTypography
} from "./chunk-XBZNPA3L.js";
import {
  getFontColor
} from "./chunk-QNPCRFUV.js";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-CWMXXUWU.js";

// src/components/TextInput.tsx
import styled from "@emotion/styled";
import React, { useState } from "react";
import { Fragment, jsx, jsxs } from "@emotion/react/jsx-runtime";
var selectLeftOffset = 10;
var iconSides = ["left", "right"];
var iconOffsets = ["small", "large"];
var iconWidths = [8, 12, 16];
function withDefaults(textInputProps) {
  return __spreadProps(__spreadValues({}, textInputProps), {
    width: textInputProps.width || "full",
    type: textInputProps.type || "text",
    disabled: Boolean(textInputProps.disabled),
    typography: __spreadValues(__spreadValues({}, defaultTextInputTypography), textInputProps.typography)
  });
}
function TextInput(textInputProps) {
  var _a, _b;
  const props = withDefaults(textInputProps);
  const [focused, setFocused] = useState(false);
  const inputRef = props.inputRef || React.createRef();
  const ref = props.inputRefCb || inputRef;
  if (props.inputRefCb && !props.inputRef) {
    console.warn("TextInput: inputRef should be provided with inputRefCb.");
  }
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && props.onEnter) {
      props.onEnter();
    }
  };
  const hasError = Boolean(props.error);
  const isIconRight = Boolean(props.icon && props.icon.side !== "left");
  const iconWidth = ((_a = props.icon) == null ? void 0 : _a.width) || 12;
  const iconCenterOffset = ((_b = props.icon) == null ? void 0 : _b.offset) === "large" ? 26 : 18;
  const iconOffset = iconCenterOffset - iconWidth / 2;
  const iconTextOffset = iconCenterOffset === 18 ? 4 : 14;
  const textInputWidth = props.width === "short" ? "250px" : "100%";
  let paddingLeftPx;
  if (typeof props.paddingX === "number") {
    if (isIconRight) {
      paddingLeftPx = props.paddingX;
    } else {
      paddingLeftPx = props.paddingX + iconWidth + iconTextOffset;
    }
  } else if (props.icon && !isIconRight) {
    paddingLeftPx = iconOffset + iconWidth + iconTextOffset;
  } else if (props.select) {
    paddingLeftPx = selectLeftOffset + props.select.width + 5;
  }
  let paddingRightPx;
  if (typeof props.paddingX === "number") {
    if (isIconRight) {
      paddingRightPx = iconTextOffset + iconWidth + props.paddingX;
    } else {
      paddingRightPx = props.paddingX;
    }
  } else if (isIconRight) {
    paddingRightPx = 28;
  }
  let input = /* @__PURE__ */ jsxs(InputContainer, { children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        disabled: props.disabled,
        maxLength: props.maxLength,
        inputMode: props.inputMode,
        pattern: props.pattern,
        onBlur: () => {
          setFocused(false);
          if (props.onBlur) {
            props.onBlur();
          }
        },
        onChange: (e) => {
          e.target.setCustomValidity("");
          props.onChange(e.target.value, e);
        },
        onFocus: () => {
          setFocused(true);
          if (props.onFocus) {
            props.onFocus();
          }
        },
        onKeyDown: (e) => {
          if (props.onKeyDown) {
            props.onKeyDown(e.key, e);
          }
          handleKeyDown(e);
        },
        id: props.id,
        onPaste: props.onPaste,
        placeholder: props.placeholder,
        ref,
        name: props.name,
        type: props.type,
        value: props.value,
        paddingLeftPx,
        paddingRightPx,
        paddingTopPx: props.paddingY,
        paddingBottomPx: props.paddingY,
        activeOutline: props.activeOutline,
        activeOutlineColor: props.activeOutlineColor,
        hasError,
        "data-test-id": props.testId,
        typography: props.typography,
        autoComplete: props.autoComplete === "off" ? "new-password" : props.autoComplete,
        onBeforeInput: (e) => {
          if (props.onBeforeInput) {
            const event = e;
            props.onBeforeInput(event);
          }
        },
        borderRadius: props.borderRadius
      }
    ),
    props.rightButtonText && /* @__PURE__ */ jsx(RightButtonAligner, { iconOffset, children: /* @__PURE__ */ jsx(RightButton, { onClick: props.onRightButtonClick, children: props.rightButtonText }) })
  ] });
  if (props.icon) {
    input = /* @__PURE__ */ jsxs(WithIcon, { hasError, withFocus: focused, children: [
      isIconRight ? /* @__PURE__ */ jsx(Fragment, { children: input }) : null,
      /* @__PURE__ */ jsx(
        TextInputIconContainer,
        {
          onClick: props.onClickIcon ? props.onClickIcon : () => {
          },
          isIconRight,
          iconOffset: typeof props.paddingX === "number" ? props.paddingX : iconOffset,
          focused,
          hasValue: Boolean(props.value),
          colorProp: props.typography.color,
          children: /* @__PURE__ */ jsx(Icon, { name: props.icon.name, width: iconWidth })
        }
      ),
      isIconRight ? null : /* @__PURE__ */ jsx(Fragment, { children: input })
    ] });
  }
  const hintTooltipId = props.hintTooltip ? `${props.id || "input"}-hint-tooltip` : void 0;
  const { select } = props;
  return /* @__PURE__ */ jsxs(StyledTextInput, { width: textInputWidth, children: [
    props.label ? /* @__PURE__ */ jsx(TextInputLabel, { hasError, children: props.label }) : null,
    select && /* @__PURE__ */ jsx(
      TextInputSelect,
      {
        value: select.value,
        widthProp: select.width,
        typography: props.typography,
        onChange: (event) => {
          var _a2;
          select.onChange(event.target.value);
          (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
        },
        children: select.options.map((option) => /* @__PURE__ */ jsx("option", { value: option.value, children: option.label }, option.value))
      }
    ),
    input,
    /* @__PURE__ */ jsx(
      InputSubtext,
      {
        text: props.error || props.hint,
        hasError,
        tooltipId: hintTooltipId
      }
    ),
    props.hintTooltip ? /* @__PURE__ */ jsx(Tooltip, { id: hintTooltipId, content: props.hintTooltip, place: "right" }) : null
  ] });
}
var TextInputSelect = styled.select`
  ${({ typography, theme }) => applyTypography(theme, typography.type, typography.size, typography.color)}
  position: absolute;
  z-index: ${z.textInput + 1};
  border: none;
  background-color: transparent;
  top: 0;
  left: ${selectLeftOffset}px;
  height: 48px;
  width: ${({ widthProp }) => `${widthProp}px`};
`;
var WithIcon = styled.div`
  ${standardBorderRadius(8)}
  position: relative;

  & ${IconContainer.toString()} {
    z-index: ${z.textInput + 1};
  }
`;
var TextInputIconContainer = styled.div`
  position: absolute;
  z-index: ${z.textInput + 1};
  ${({ isIconRight, iconOffset }) => isIconRight ? `right: ${iconOffset}px` : `left: ${iconOffset}px`};
  top: 0;
  bottom: 0;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: ${({ onClick }) => onClick ? "pointer" : "auto"};
  color: ${({ focused, hasValue, theme, colorProp }) => focused || hasValue ? getFontColor(theme, colorProp) : textInputPlaceholderColor({ theme })};
`;
var Input = styled.input`
  ${textInputStyle};

  // disable autofill styles in chrome https://stackoverflow.com/a/68240841/9808766
  &:-webkit-autofill,
  &:-webkit-autofill:focus,
  &:-internal-autofill-selected,
  &:-internal-autofill-selected:focus {
    background-color: ${({ theme }) => theme.bg} !important;
    color: ${({ theme }) => theme.text} !important;
    transition:
      background-color 600000s 0s,
      color 600000s 0s !important;
  }
`;
var RightButtonAligner = styled.div`
  position: absolute;
  top: 0;
  right: ${({ iconOffset }) => iconOffset}px;
  z-index: ${z.textInput + 1};
  bottom: 0;
  display: flex;
  align-items: center;
`;
var RightButton = styled(UnstyledButton)`
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.vlcNeutral};
  color: ${({ theme }) => theme.mcNeutral};
  font-size: 14px;
  font-weight: 600;
  padding: 8px 10px;
`;
var TextInputLabel = styled.label`
  ${standardBorderRadius(8)}
  font-size: 10px;
  position: absolute;
  z-index: ${z.textInput + 1};
  background-color: ${({ theme }) => theme.bg};
  color: ${({ theme, hasError }) => hasError ? theme.danger : theme.mcNeutral};
  font-weight: 600;
  padding: 4px 6px;
  left: 12px;
  top: -10px;
`;
var TextInputHalfRow = styled.div`
  margin-top: ${inputSpacingPx}px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  & > * {
    width: 50%;
  }
`;
var StyledTextInput = styled.div`
  width: ${({ width }) => width};
  position: relative;

  /* eg forms, should be left consistent: */
  & + & {
    margin-top: ${inputSpacingPx}px;
  }

  ${TextInputHalfRow.toString()} & + & {
    margin-top: 0;
  }

  ${CheckboxContainer.toString()} + &,
  ${ToggleContainer.toString()} + &,
  & + ${CheckboxContainer.toString()},
  & + ${ToggleContainer.toString()} {
    margin-top: ${inputSpacingPx}px;
  }
`;
var InputContainer = styled.div`
  position: relative;
`;

export {
  iconSides,
  iconOffsets,
  iconWidths,
  TextInput,
  TextInputHalfRow
};
